import React, { useEffect, useState } from "react";
import api from "../../api";
import { Loader2, Plus, MapPin, Pencil, Check, X } from "lucide-react";
import { toast } from "../../hooks/use-toast";

export default function CitiesTab() {
  const [cities, setCities] = useState(null);
  const [form, setForm] = useState({ name: "", lat: "", lng: "" });
  const [edit, setEdit] = useState(null); // { slug, name, lat, lng }

  const load = () => api.get("/cities").then(({ data }) => setCities(Array.isArray(data) ? data : [])).catch(() => setCities([]));
  useEffect(() => { load(); }, []);

  const add = async () => {
    const name = form.name.trim();
    if (!name) return;
    try {
      const payload = { name };
      if (form.lat !== "") payload.lat = parseFloat(form.lat);
      if (form.lng !== "") payload.lng = parseFloat(form.lng);
      const { data } = await api.post("/business/cities", payload);
      setCities(data.cities);
      setForm({ name: "", lat: "", lng: "" });
      toast({ title: "City added", description: `${name} is now available to tag events with.` });
    } catch (e) { toast({ title: "Error", description: e?.response?.data?.detail || "Could not add city", variant: "destructive" }); }
  };

  const saveEdit = async () => {
    try {
      const payload = {};
      if (edit.name && edit.name.trim()) payload.name = edit.name.trim();
      payload.lat = edit.lat === "" ? null : parseFloat(edit.lat);
      payload.lng = edit.lng === "" ? null : parseFloat(edit.lng);
      const { data } = await api.put(`/business/cities/${edit.slug}`, payload);
      setCities(data.cities);
      setEdit(null);
      toast({ title: "City updated" });
    } catch (e) { toast({ title: "Error", description: e?.response?.data?.detail || "Could not update", variant: "destructive" }); }
  };

  if (!cities) return <Loader2 className="animate-spin text-[#2ec9c9]" size={32} />;

  return (
    <div data-testid="cities-tab" className="max-w-[640px]">
      <div className="bg-white rounded-2xl p-6 shadow-[0_8px_24px_rgba(20,30,55,0.06)]">
        <h3 className="font-semibold text-[#16243b] mb-1">Cities / regions</h3>
        <p className="text-xs text-[#8a97a8] mb-4">Add the cities you run events in so customers can filter by location. Add latitude &amp; longitude to power "events near me". Cities are shared across the platform.</p>

        <div className="space-y-1.5 mb-4">
          {cities.map((c) => (
            <div key={c.slug} data-testid={`city-row-${c.slug}`} className="rounded-lg bg-[#f6f8fb] px-3 py-2.5">
              {edit && edit.slug === c.slug ? (
                <div className="flex flex-wrap items-center gap-2">
                  <input value={edit.name} onChange={(e) => setEdit({ ...edit, name: e.target.value })} data-testid="city-edit-name"
                    className="flex-1 min-w-[120px] rounded-lg border border-gray-200 px-3 py-1.5 text-sm outline-none focus:border-[#2ec9c9]" />
                  <input type="number" step="any" value={edit.lat} onChange={(e) => setEdit({ ...edit, lat: e.target.value })} placeholder="Lat" data-testid="city-edit-lat"
                    className="w-24 rounded-lg border border-gray-200 px-3 py-1.5 text-sm outline-none focus:border-[#2ec9c9]" />
                  <input type="number" step="any" value={edit.lng} onChange={(e) => setEdit({ ...edit, lng: e.target.value })} placeholder="Lng" data-testid="city-edit-lng"
                    className="w-24 rounded-lg border border-gray-200 px-3 py-1.5 text-sm outline-none focus:border-[#2ec9c9]" />
                  <button onClick={saveEdit} data-testid="city-edit-save" className="text-[#1fb0b0] p-1.5 hover:bg-[#eefafa] rounded"><Check size={16} /></button>
                  <button onClick={() => setEdit(null)} className="text-[#8a97a8] p-1.5 hover:bg-gray-100 rounded"><X size={16} /></button>
                </div>
              ) : (
                <div className="flex items-center justify-between">
                  <span className="text-[#16243b] flex items-center gap-2">
                    <MapPin size={14} className="text-[#2ec9c9]" /> <span className="font-medium">{c.name}</span>
                    <code className="text-[10px] text-[#8a97a8]">/city/{c.slug}</code>
                    {(typeof c.lat === "number" && typeof c.lng === "number")
                      ? <span className="text-[10px] text-[#2ec9c9]">· geo ✓</span>
                      : <span className="text-[10px] text-amber-500">· no coords</span>}
                  </span>
                  <button onClick={() => setEdit({ slug: c.slug, name: c.name, lat: c.lat ?? "", lng: c.lng ?? "" })} data-testid={`city-edit-${c.slug}`}
                    className="text-xs text-[#5a6b80] hover:text-[#1fb0b0] flex items-center gap-1 font-medium"><Pencil size={12} /> Edit</button>
                </div>
              )}
            </div>
          ))}
          {cities.length === 0 && <p className="text-sm text-[#8a97a8]">No cities yet.</p>}
        </div>

        <div className="flex flex-wrap gap-2 items-center border-t border-gray-100 pt-4">
          <input value={form.name} onChange={(e) => setForm({ ...form, name: e.target.value })} onKeyDown={(e) => e.key === "Enter" && add()} data-testid="city-new-name"
            placeholder="City name" className="flex-1 min-w-[140px] rounded-lg border border-gray-200 px-3 py-2 text-sm outline-none focus:border-[#2ec9c9]" />
          <input type="number" step="any" value={form.lat} onChange={(e) => setForm({ ...form, lat: e.target.value })} data-testid="city-new-lat"
            placeholder="Lat" className="w-24 rounded-lg border border-gray-200 px-3 py-2 text-sm outline-none focus:border-[#2ec9c9]" />
          <input type="number" step="any" value={form.lng} onChange={(e) => setForm({ ...form, lng: e.target.value })} data-testid="city-new-lng"
            placeholder="Lng" className="w-24 rounded-lg border border-gray-200 px-3 py-2 text-sm outline-none focus:border-[#2ec9c9]" />
          <button onClick={add} data-testid="city-add-btn" className="gp-btn-teal rounded-lg px-4 py-2 text-sm font-medium flex items-center gap-1"><Plus size={15} /> Add</button>
        </div>
      </div>
    </div>
  );
}
